import { useState } from "react";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const initial = {
  full_name: "",
  phone: "",
  email: "",
  check_in: "",
  check_out: "",
  flexible_dates: false,
  adults: 2,
  children: 0,
  purpose: "",
  stay_type: "short",
  meal_requirements: "",
  special_requirements: "",
  message: "",
  consent: false,
};

export default function EnquiryForm() {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = "Please enter your name";
    if (!/^\+?[0-9 \-()]{7,}$/.test(form.phone.trim())) e.phone = "Please enter a valid phone";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Please enter a valid email";
    if (!form.consent) e.consent = "Please confirm consent to continue";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await api.post("/enquiries", {
        ...form,
        adults: Number(form.adults) || 0,
        children: Number(form.children) || 0,
      });
      setSuccess(true);
      setForm(initial);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <section id="enquire" className="py-24 md:py-32 bg-[#F7F5F0]">
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-center">
          <div
            data-testid="enquiry-success"
            className="bg-[#EFEAE4] border border-[#6A7B61]/30 rounded-2xl p-12 md:p-16"
          >
            <CheckCircle2 className="mx-auto text-[#6A7B61]" size={48} strokeWidth={1.4} />
            <h3 className="serif text-3xl md:text-4xl text-[#2C392F] mt-6 leading-tight">
              Thank you for considering ChiMano House.&nbsp;
            </h3>
            <p className="mt-5 text-[#4A4D4A] leading-relaxed">
              We will get in touch with you shortly to discuss your stay.
            </p>
            <button
              data-testid="enquiry-send-another"
              onClick={() => setSuccess(false)}
              className="mt-8 text-[12px] uppercase tracking-[0.25em] text-[#985F47] hover:text-[#86513A]"
            >
              Send another enquiry
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="enquire" className="py-24 md:py-32 bg-[#F7F5F0]">
      <div className="max-w-5xl mx-auto px-6 md:px-10">
        <div className="text-center mb-12 reveal">
          <div className="text-[11px] uppercase tracking-[0.35em] text-[#985F47] mb-5">Enquire</div>
          <h2 className="serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tightest text-[#2C392F] font-light">
            Plan your stay at <span className="italic text-[#985F47]">ChiMano House.</span>
          </h2>
          <p className="mt-5 text-[#4A4D4A] max-w-xl mx-auto">
            Share a few details and we&apos;ll get back to you to plan a stay that fits your pace.
          </p>
        </div>

        <form
          data-testid="enquiry-form"
          onSubmit={submit}
          className="reveal bg-[#FBF9F4] border border-[#2C392F]/10 rounded-2xl p-8 md:p-12 grid grid-cols-1 md:grid-cols-2 gap-7"
          noValidate
        >
          <Field label="Full name" error={errors.full_name}>
            <input
              data-testid="enquiry-input-full_name"
              value={form.full_name}
              onChange={(e) => set("full_name", e.target.value)}
              className={inputCls}
              placeholder="Your name"
            />
          </Field>

          <Field label="Phone number" error={errors.phone}>
            <input
              data-testid="enquiry-input-phone"
              value={form.phone}
              onChange={(e) => set("phone", e.target.value)}
              className={inputCls}
              placeholder="+91"
              inputMode="tel"
            />
          </Field>

          <Field label="Email address" error={errors.email} className="md:col-span-2">
            <input
              data-testid="enquiry-input-email"
              type="email"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              className={inputCls}
              placeholder="you@example.com"
            />
          </Field>

          <Field label="Check-in date">
            <input
              data-testid="enquiry-input-check_in"
              type="date"
              value={form.check_in}
              onChange={(e) => set("check_in", e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label="Check-out date">
            <input
              data-testid="enquiry-input-check_out"
              type="date"
              value={form.check_out}
              onChange={(e) => set("check_out", e.target.value)}
              className={inputCls}
            />
          </Field>

          <div className="md:col-span-2 -mt-3 flex items-center gap-3">
            <input
              id="flexible_dates"
              data-testid="enquiry-input-flexible_dates"
              type="checkbox"
              checked={form.flexible_dates}
              onChange={(e) => set("flexible_dates", e.target.checked)}
              className="h-4 w-4 accent-[#2C392F]"
            />
            <label htmlFor="flexible_dates" className="text-sm text-[#1A1C1A]">
              My dates are flexible
            </label>
          </div>

          <Field label="Number of adults">
            <input
              data-testid="enquiry-input-adults"
              type="number"
              min={1}
              value={form.adults}
              onChange={(e) => set("adults", e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label="Number of children">
            <input
              data-testid="enquiry-input-children"
              type="number"
              min={0}
              value={form.children}
              onChange={(e) => set("children", e.target.value)}
              className={inputCls}
            />
          </Field>

          <Field label="Purpose of stay">
            <input
              data-testid="enquiry-input-purpose"
              value={form.purpose}
              onChange={(e) => set("purpose", e.target.value)}
              className={inputCls}
              placeholder="Family visit, remote work, holiday…"
            />
          </Field>

          <Field label="Stay type">
            <select
              data-testid="enquiry-input-stay_type"
              value={form.stay_type}
              onChange={(e) => set("stay_type", e.target.value)}
              className={inputCls}
            >
              <option value="short">Short stay</option>
              <option value="extended">Extended stay</option>
            </select>
          </Field>

          <Field label="Meal requirements" className="md:col-span-2">
            <input
              data-testid="enquiry-input-meal_requirements"
              value={form.meal_requirements}
              onChange={(e) => set("meal_requirements", e.target.value)}
              className={inputCls}
              placeholder="Veg / non-veg / children / elderly-friendly"
            />
          </Field>

          <Field label="Special requirements" className="md:col-span-2">
            <input
              data-testid="enquiry-input-special_requirements"
              value={form.special_requirements}
              onChange={(e) => set("special_requirements", e.target.value)}
              className={inputCls}
              placeholder="Anything we should know"
            />
          </Field>

          <Field label="Message" className="md:col-span-2">
            <textarea
              data-testid="enquiry-input-message"
              value={form.message}
              onChange={(e) => set("message", e.target.value)}
              rows={4}
              className={`${inputCls} resize-none pt-3`}
              placeholder="Tell us a little about the stay you're planning…"
            />
          </Field>

          <div className="md:col-span-2 flex items-start gap-3">
            <input
              id="consent"
              data-testid="enquiry-input-consent"
              type="checkbox"
              checked={form.consent}
              onChange={(e) => set("consent", e.target.checked)}
              className="h-4 w-4 mt-1 accent-[#2C392F]"
            />
            <label htmlFor="consent" className="text-sm text-[#1A1C1A]">
              I agree to be contacted regarding my enquiry and consent to ChiMano House storing my
              details to respond to me.
              {errors.consent && (
                <span className="block text-[#985F47] mt-1 text-xs">{errors.consent}</span>
              )}
            </label>
          </div>

          <div className="md:col-span-2 mt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <p className="text-xs text-[#4A4D4A] italic max-w-md">
              We do not share your details. We&apos;ll reach out within a day to discuss availability and
              arrangements.
            </p>
            <button
              type="submit"
              disabled={loading}
              data-testid="enquiry-submit-button"
              className="inline-flex items-center justify-center gap-2 bg-[#2C392F] text-[#F7F5F0] hover:bg-[#1E2823] disabled:opacity-60 px-7 py-4 rounded-full text-[12px] tracking-[0.2em] uppercase transition-all"
            >
              {loading ? "Sending…" : "Send Stay Enquiry"} {!loading && <ArrowRight size={16} />}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

const inputCls =
  "w-full bg-transparent border-b border-[#2C392F]/25 focus:border-[#2C392F] outline-none px-0 py-3 text-[15px] text-[#1A1C1A] placeholder:text-[#1A1C1A]/30 transition-colors";

function Field({ label, children, error, className = "" }) {
  return (
    <div className={className}>
      <label className="block text-[10px] uppercase tracking-[0.3em] text-[#985F47] mb-2">{label}</label>
      {children}
      {error && <div className="mt-1 text-xs text-[#985F47]">{error}</div>}
    </div>
  );
}
